class TempoStickyAtc extends HTMLElement {
  constructor() {
    super();
    this.purchaseOption = null;
    this.addButton = null;
    this.buttonLabel = null;
    this.selectionLabel = null;
    this.priceLabel = null;
    this.detailLabel = null;
    this.status = null;
    this.buyButtonsObserver = null;
    this.footerObserver = null;
    this.loadingTimeout = null;
    this.pendingCartCleanup = null;
    this.buyButtonsPassed = false;
    this.footerVisible = false;
  }

  connectedCallback() {
    this.purchaseOption = this.querySelector('[data-sticky-purchase-option]');
    this.addButton = this.querySelector('[data-sticky-add-to-cart]');
    this.buttonLabel = this.querySelector('[data-sticky-button-label]');
    this.selectionLabel = this.querySelector('[data-sticky-selection-label]');
    this.priceLabel = this.querySelector('[data-sticky-selection-price]');
    this.detailLabel = this.querySelector('[data-sticky-selection-detail]');
    this.status = this.querySelector('[data-sticky-status]');

    if (!this.purchaseOption || !this.addButton) return;

    this.purchaseOption.addEventListener('change', this.handlePurchaseOptionChange);
    this.addButton.addEventListener('click', this.handleAddToCart);

    this.bindMainPurchasePicker();
    this.updateSelectionDisplay();
    this.applySelectionToProductForm();
    this.setupVisibilityObservers();
  }

  disconnectedCallback() {
    this.purchaseOption?.removeEventListener('change', this.handlePurchaseOptionChange);
    this.addButton?.removeEventListener('click', this.handleAddToCart);
    this.buyButtonsObserver?.disconnect();
    this.footerObserver?.disconnect();
    this.pendingCartCleanup?.();
    if (this.loadingTimeout) window.clearTimeout(this.loadingTimeout);
  }

  getProductFormComponent() {
    const productSection = document.querySelector('#product-information-bg');
    const scopedForms = productSection?.querySelectorAll('product-form-component') || [];
    const allForms = scopedForms.length ? scopedForms : document.querySelectorAll('product-form-component');

    return Array.from(allForms).find(
      (form) => form.dataset.productId === this.dataset.productId
    ) || null;
  }

  getProductForm() {
    return this.getProductFormComponent()?.querySelector('form') || null;
  }

  getSelectedOption() {
    return this.purchaseOption?.options[this.purchaseOption.selectedIndex] || null;
  }

  handlePurchaseOptionChange = () => {
    this.updateSelectionDisplay();
    this.applySelectionToProductForm();
    this.syncMainPurchasePickerVisuals();
  };

  updateSelectionDisplay() {
    const option = this.getSelectedOption();
    if (!option) return;

    if (this.selectionLabel) this.selectionLabel.textContent = option.textContent.trim();
    if (this.priceLabel) this.priceLabel.textContent = option.dataset.priceLabel || '';
    if (this.detailLabel) this.detailLabel.textContent = option.dataset.detail || '';

    const available = option.dataset.available === 'true';
    this.addButton.disabled = !available;
    this.addButton.setAttribute('aria-disabled', String(!available));
  }

  applySelectionToProductForm() {
    const form = this.getProductForm();
    const option = this.getSelectedOption();
    if (!form || !option) return false;

    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = option.dataset.variantId || '';

    let sellingPlanInput = Array.from(form.elements).find((element) => element.name === 'selling_plan');
    if (!sellingPlanInput && option.dataset.sellingPlanId) {
      sellingPlanInput = document.createElement('input');
      sellingPlanInput.type = 'hidden';
      sellingPlanInput.name = 'selling_plan';
      sellingPlanInput.dataset.stickyGenerated = 'true';
      form.appendChild(sellingPlanInput);
    }
    if (sellingPlanInput) sellingPlanInput.value = option.dataset.sellingPlanId || '';

    return true;
  }

  bindMainPurchasePicker() {
    const productForm = this.getProductFormComponent();
    const section = productForm?.closest('.shopify-section');
    const mainPicker = section?.querySelector('selling-plan-picker');
    if (!mainPicker) return;

    mainPicker.addEventListener('change', (event) => {
      const radio = event.target.closest('input[type="radio"]');
      if (!radio || !this.purchaseOption) return;

      const subscriptionOption = Array.from(this.purchaseOption.options).find(
        (option) => option.dataset.sellingPlanId === radio.value
      );
      if (!subscriptionOption) return;

      this.purchaseOption.value = subscriptionOption.value;
      this.updateSelectionDisplay();
    });

    mainPicker.querySelector('[data-otp-buy]')?.addEventListener('click', () => {
      if (!this.purchaseOption) return;
      this.purchaseOption.value = 'one-time';
      this.updateSelectionDisplay();
    });
  }

  syncMainPurchasePickerVisuals() {
    const productForm = this.getProductFormComponent();
    const section = productForm?.closest('.shopify-section');
    const mainPicker = section?.querySelector('selling-plan-picker');
    const option = this.getSelectedOption();
    if (!mainPicker || !option) return;

    const cards = mainPicker.querySelectorAll('.selling-plan-picker__card');
    const radios = mainPicker.querySelectorAll('input[type="radio"]');
    const oneTimeButton = mainPicker.querySelector('[data-otp-buy]');

    cards.forEach((card) => card.classList.remove('selling-plan-picker__card--selected'));
    radios.forEach((radio) => { radio.checked = false; });

    if (option.value === 'subscription') {
      const matchingRadio = Array.from(radios).find(
        (radio) => radio.value === option.dataset.sellingPlanId
      );
      if (matchingRadio) {
        matchingRadio.checked = true;
        matchingRadio.closest('.selling-plan-picker__card')?.classList.add('selling-plan-picker__card--selected');
        mainPicker.updateAddToCartPrice?.(matchingRadio);
      }
    } else {
      oneTimeButton?.classList.add('selling-plan-picker__card--selected');
      const price = Number(oneTimeButton?.dataset.price);
      const comparePrice = Number(oneTimeButton?.dataset.comparePrice);
      if (Number.isFinite(price)) mainPicker.updatePriceDisplay?.(price, comparePrice);
    }
  }

  handleAddToCart = () => {
    const productForm = this.getProductFormComponent();
    const form = productForm?.querySelector('form');
    const option = this.getSelectedOption();

    if (!productForm || !form || !option || !this.applySelectionToProductForm()) {
      this.setStatus('Choose a purchase option on the product form.');
      return;
    }

    if (option.dataset.available !== 'true') {
      this.setStatus('This option is currently unavailable.');
      return;
    }

    this.addButton.dataset.defaultLabel = this.buttonLabel?.textContent || 'Add';
    this.pendingCartCleanup?.();
    this.setLoading(true);

    let cleanup;
    const finish = (event) => {
      const eventProductId = event.detail?.data?.productId;
      if (eventProductId && String(eventProductId) !== String(this.dataset.productId)) return;

      cleanup();
      this.setLoading(false);
      if (event.type === 'cart:error') {
        this.setStatus(event.detail?.data?.message || 'Unable to add this item. Please try again.');
        return;
      }

      this.setStatus('Added to cart.');
      if (this.buttonLabel) this.buttonLabel.textContent = 'Added';
      window.setTimeout(() => {
        if (this.buttonLabel) this.buttonLabel.textContent = this.addButton.dataset.defaultLabel || 'Add';
      }, 1600);
    };

    cleanup = () => {
      document.removeEventListener('cart:update', finish);
      document.removeEventListener('cart:error', finish);
      if (this.loadingTimeout) {
        window.clearTimeout(this.loadingTimeout);
        this.loadingTimeout = null;
      }
      if (this.pendingCartCleanup === cleanup) this.pendingCartCleanup = null;
    };

    this.pendingCartCleanup = cleanup;
    document.addEventListener('cart:update', finish);
    document.addEventListener('cart:error', finish);

    try {
      form.requestSubmit();
    } catch (error) {
      cleanup();
      this.setLoading(false);
      this.setStatus('Unable to add this item. Please try again.');
      console.error(error);
      return;
    }

    this.loadingTimeout = window.setTimeout(() => {
      cleanup();
      this.setLoading(false);
      this.setStatus('The cart is taking longer than expected. Please try again.');
    }, 8000);
  };

  setLoading(isLoading) {
    if (this.loadingTimeout) {
      window.clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }

    this.addButton.disabled = isLoading;
    this.addButton.setAttribute('aria-busy', String(isLoading));
    this.classList.toggle('is-loading', isLoading);

    if (this.buttonLabel && isLoading) this.buttonLabel.textContent = 'Adding…';
    if (this.buttonLabel && !isLoading && this.buttonLabel.textContent === 'Adding…') {
      this.buttonLabel.textContent = this.addButton.dataset.defaultLabel || 'Add';
    }
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }

  setupVisibilityObservers() {
    const productForm = this.getProductFormComponent();
    const buyButtons = productForm?.closest('.buy-buttons-block');
    const footer = document.querySelector('footer, .Footer_main_new');
    if (!buyButtons) return;

    this.buyButtonsObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      this.buyButtonsPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      this.updateVisibility();
    });
    this.buyButtonsObserver.observe(buyButtons);

    if (footer) {
      this.footerObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        this.footerVisible = entry.isIntersecting;
        this.updateVisibility();
      }, { rootMargin: '120px 0px 0px 0px' });
      this.footerObserver.observe(footer);
    }
  }

  updateVisibility() {
    this.classList.toggle('sticky-active', this.buyButtonsPassed && !this.footerVisible);
  }
}

if (!customElements.get('tempo-sticky-atc')) {
  customElements.define('tempo-sticky-atc', TempoStickyAtc);
}
